import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { AppDispatch } from "../../store";

import { whtsappBg, whtsappIcon, doubleTick } from "../../asset";
import { fetchTemplateLibrary } from "../../features/template/TemplateService";
import { WhatsappSendItem } from "../../types/TemplateType";
import { FiCornerDownLeft, FiLink, FiPhone, FiFile } from "react-icons/fi";

interface ExploreTemplateListProps {
  selectedTypes: string[];

  onLoadingChange?: (loading: boolean) => void;
  onPageInfoChange?: (pageInfo: any) => void;

  page: number;
  pageSize: number;
  cursor?: string;
}

/* ---------- Helpers ---------- */

const getHeaderFormat = (components: any[]): string => {
  const header = components?.find((c: any) => c.type === "HEADER");
  if (!header || !header.format) return "Text";
  return header.format.charAt(0) + header.format.slice(1).toLowerCase();
};

const InfoRow = ({ label, value }: { label: string; value?: string }) => (
  <div className="flex text-sm gap-1">
    <span className="text-text-secondary font-semibold shrink-0">{label}:</span>
    <span className="text-black font-bold break-all">{value || "-"}</span>
  </div>
);

export default function ExploreTemplateList({
  selectedTypes,
  onLoadingChange,
  onPageInfoChange,
  page,
  pageSize,
  cursor,
}: ExploreTemplateListProps) {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const [templates, setTemplates] = useState<WhatsappSendItem[]>([]);

  const loadTemplates = async () => {
    try {
      onLoadingChange?.(true);
      const result = await dispatch(
        fetchTemplateLibrary({ size: pageSize, after: cursor })
      ).unwrap();

      setTemplates(result.results || []);
      onPageInfoChange?.(result.page); // push pageInfo up to parent
    } catch (err) {
      console.error("Template library fetch failed", err);
    } finally {
      onLoadingChange?.(false);
    }
  };

  // Re-fetch whenever page or pageSize changes
  useEffect(() => { loadTemplates(); }, [page, pageSize, cursor]);

  const filteredTemplates = templates.filter((template) => {
    if (selectedTypes.includes("All")) return true;
    return selectedTypes.includes(getHeaderFormat(template.components || []));
  });

  if (filteredTemplates.length === 0) {
    return (
      <div className="flex items-center justify-center w-full py-16 text-text-secondary font-semibold">
        No example templates found for the selected filters.
      </div>
    );
  }

  return (
    <>
      {filteredTemplates.map((template) => {
        const header = template.components?.find((c: any) => c.type === "HEADER");
        const footerComp = template.components?.find((c: any) => c.type === "FOOTER");
        const buttonsComp = template.components?.find((c: any) => c.type === "BUTTONS");
        const bodyComp = template.components?.find((c: any) => c.type === "BODY");

        const headerFormat = getHeaderFormat(template.components || []);
        const bodyText = template.preview || bodyComp?.text || "";
        const footerText = footerComp?.text || "";

        const allButtons = buttonsComp?.buttons || [];
        const ctaButtons = allButtons.filter((b: any) => b.type === "URL" || b.type === "PHONE_NUMBER");
        const replyButtons = allButtons.filter((b: any) => b.type === "QUICK_REPLY");

        return (
          <div
            key={template.id}
            className="flex flex-row border rounded-tl-4xl rounded-br-4xl rounded-2xl shadow-md w-full sm:max-w-[280px] overflow-hidden"
          >
            <div className="flex flex-col gap-4 p-4 flex-1 min-w-0">

              {/* WhatsApp Preview */}
              <div className="rounded-2xl overflow-hidden border border-gray-200">

                {/* Top bar */}
                <div className="flex items-center gap-2 px-3 py-2 bg-white border-b border-gray-100">
                  <img src={whtsappIcon} className="w-4 h-4" alt="WhatsApp" />
                  <span className="text-xs font-semibold text-black">
                    Whatsapp Business
                  </span>
                </div>

                {/* Chat background */}
                <div
                  className="p-3 bg-cover bg-center"
                  style={{ backgroundImage: `url(${whtsappBg})` }}
                >
                  <div className="bg-[#E4FEE0] rounded-xl overflow-hidden shadow-sm w-full">

                    {/* Header */}
                    {header?.format === "TEXT" && header.text && (
                      <div className="px-3 pt-2 text-xs font-bold text-black">
                        {header.text}
                      </div>
                    )}

                    {header?.format === "IMAGE" && (
                      <div className="w-full h-20 bg-gray-200 flex items-center justify-center text-xs text-gray-400">
                        📷 Image
                      </div>
                    )}

                    {header?.format === "VIDEO" && (
                      <div className="w-full h-20 bg-gray-200 flex items-center justify-center text-xs text-gray-400">
                        🎥 Video
                      </div>
                    )}

                    {header?.format === "DOCUMENT" && (
                      <div className="flex items-center gap-2 px-3 pt-2 text-xs text-blue-600">
                        <FiFile />
                        Document
                      </div>
                    )}

                    {/* Body */}
                    <div className="px-3 py-2 text-xs text-black whitespace-pre-wrap break-words">
                      {bodyText || "No body text"}
                    </div>

                    {/* Footer */}
                    {footerText && (
                      <div className="px-3 pb-1 text-[10px] text-gray-400 italic">
                        {footerText}
                      </div>
                    )}

                    {/* Timestamp */}
                    <div className="flex items-center justify-end gap-1 px-3 pb-2 text-[10px] text-gray-400">
                      <span>20:13</span>
                      <img src={doubleTick} className="w-3 h-3" alt="Delivered" />
                    </div>

                    {/* CTA Buttons */}
                    {ctaButtons.map((btn: any, i: number) => (
                      <div
                        key={i}
                        className="flex items-center justify-center gap-1 py-1.5 text-[#00A5F4] text-[10px] font-semibold border-b border-green-100 last:border-b-0"
                      >
                        {btn.type === "URL" && <FiLink size={14} />}
                        {btn.type === "PHONE_NUMBER" && <FiPhone size={14} />}
                        <span>{btn.text}</span>
                      </div>
                    ))}
                  </div>

                  {/* Quick reply buttons */}
                  {replyButtons.length > 0 && (
                    <div className="flex flex-col gap-1 mt-2">
                      {replyButtons.map((btn: any, i: number) => (
                        <div
                          key={i}
                          className="bg-white text-[#00A5F4] text-[10px] font-semibold py-1.5 px-3 rounded-lg shadow-sm border border-gray-200 text-center w-full flex items-center justify-center gap-1"
                        >
                          <FiCornerDownLeft size={14} />
                          {btn.text}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Template Info */}
              <div className="flex flex-col gap-1.5">
                <span className="text-base font-bold text-brand-main truncate">
                  {template.title}
                </span>
                {template.description && (
                  <p className="text-xs text-text-secondary line-clamp-2">
                    {template.description}
                  </p>
                )}

                <InfoRow label="Category" value={template.category} />
                <InfoRow label="Industry" value={template.industry} />
                <InfoRow label="Language" value={template.language} />
                <InfoRow label="Type" value={headerFormat} />
              </div>

              <button
                onClick={() =>
                  navigate("/admin/variables-templates/add", { state: { libraryTemplate: template } })
                }
                className="mt-auto w-full py-2 rounded-4xl bg-brand-blue-gradient text-white text-sm font-bold"
              >
                Use this template
              </button>
            </div>
          </div>
        );
      })}
    </>
  );
}